import { Fragment, useEffect, useState } from "react";
import { ArrowRight, ChevronDown, Menu, Search, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import logo from "@/assets/inext-logo.svg";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { buildSiteNav, type NavEntry, type NavFeatured, type NavLeaf } from "./nav-config";
import { useSite } from "@/hooks/use-site";
import { isPublicPathActive, localizeUrl, type Locale } from "@/lib/site";
import { cn } from "@/lib/utils";

export function SiteHeader({
  languagePaths,
  homeGlass = false,
}: {
  languagePaths?: Partial<Record<Locale, string>>;
  homeGlass?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState<string | null>(null);
  const [overHomeHero, setOverHomeHero] = useState(homeGlass);
  const { messages, site, href } = useSite();

  const u = (path: string) => localizeUrl(path, site.locale, href);
  const home = u("/");
  const search = u("/search");
  const contactSales = u("/contacts?inquiry=sales#inquiry-form");
  const nav = buildSiteNav({ m: messages, u });

  const linkActive = (target: string) => isPublicPathActive(href, target, home);
  const menuActive = (entry: Extract<NavEntry, { kind: "menu" }>) =>
    linkActive(entry.overviewHref) || entry.items.some((item) => linkActive(item.href));

  const closeMobile = () => {
    setOpen(false);
    setOpenMobileMenu(null);
  };

  useEffect(() => {
    if (!homeGlass) return;

    const hero = document.querySelector<HTMLElement>("[data-home-hero]");
    if (!hero) {
      setOverHomeHero(false);
      return;
    }

    const headerHeight = 64;
    const hysteresis = 8;
    let animationFrame = 0;

    const update = () => {
      animationFrame = 0;
      const heroBottom = hero.getBoundingClientRect().bottom;
      setOverHomeHero((current) =>
        current ? heroBottom > headerHeight - hysteresis : heroBottom > headerHeight + hysteresis,
      );
    };

    const requestUpdate = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, [homeGlass]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMobile();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const showHomeGlass = homeGlass && overHomeHero;
  const activeUnderline =
    "relative text-foreground after:absolute after:inset-x-2.5 after:bottom-1 after:h-0.5 after:rounded-full after:bg-primary";

  return (
    <>
      <header
        className={cn(
          "site-header sticky top-0 z-50 w-full border-b",
          showHomeGlass && "site-header-home-glass",
        )}
      >
        <div className="relative z-10 mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <a href={home} className="flex shrink-0 items-center gap-2 group">
            <img
              src={logo}
              alt="inext"
              className="h-7 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </a>

          <NavigationMenu
            className="hidden lg:flex"
            onValueChange={(value) => {
              // Pauses paint-heavy looping animations (see [data-nav-menu-open]
              // rules in styles.css) so dropdown content paints in one frame.
              document.documentElement.toggleAttribute("data-nav-menu-open", Boolean(value));
            }}
          >
            <NavigationMenuList className="gap-0.5">
              {nav.map((entry) =>
                entry.kind === "link" ? (
                  <NavigationMenuItem key={entry.label}>
                    <NavigationMenuLink asChild>
                      <a
                        href={entry.href}
                        className={cn(
                          "site-header-nav-link flex h-10 items-center whitespace-nowrap rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40",
                          linkActive(entry.href) && activeUnderline,
                        )}
                      >
                        {entry.label}
                      </a>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                ) : (
                  <NavigationMenuItem key={entry.id}>
                    <NavigationMenuTrigger
                      href={entry.overviewHref}
                      className={cn(
                        "site-header-nav-link h-10 whitespace-nowrap px-2.5 text-sm font-normal text-muted-foreground transition-colors hover:text-foreground focus:text-foreground data-[state=open]:text-foreground",
                        menuActive(entry) && activeUnderline,
                      )}
                    >
                      {entry.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <MenuPanel entry={entry} linkActive={linkActive} />
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                ),
              )}
            </NavigationMenuList>
          </NavigationMenu>

          <div className="flex shrink-0 items-center gap-2">
            <a
              href={search}
              className="site-header-control hidden h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 lg:inline-flex"
              aria-label={messages.navSearch}
              title={messages.navSearch}
            >
              <Search className="h-4 w-4" />
            </a>
            <LanguageSwitcher
              paths={languagePaths}
              triggerClassName={showHomeGlass ? "site-header-control" : undefined}
            />
            <Button
              asChild
              className="hidden lg:inline-flex h-9 whitespace-nowrap rounded-lg px-4 font-medium animate-glow"
            >
              <a href={contactSales}>{messages.contactSales}</a>
            </Button>
            <button
              type="button"
              className="site-header-control lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border border-border"
              onClick={() => (open ? closeMobile() : setOpen(true))}
              aria-label={messages.toggleMenu}
              aria-expanded={open}
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="mobile-nav-panel absolute inset-x-0 top-full z-50 lg:hidden border-t border-border bg-background animate-in fade-in-0 slide-in-from-top-2 duration-200">
            <div className="mx-auto flex max-h-[calc(100vh-4rem)] max-w-7xl flex-col gap-1 overflow-y-auto px-6 py-4">
              {nav.map((entry) =>
                entry.kind === "link" ? (
                  <a
                    key={entry.label}
                    href={entry.href}
                    onClick={closeMobile}
                    className={cn(
                      "rounded-md px-2 py-2 text-sm hover:bg-muted",
                      linkActive(entry.href) ? "font-medium text-primary" : "text-foreground",
                    )}
                  >
                    {entry.label}
                  </a>
                ) : (
                  <Collapsible
                    key={entry.id}
                    open={openMobileMenu === entry.id}
                    onOpenChange={(value) => setOpenMobileMenu(value ? entry.id : null)}
                    className="flex flex-col gap-0.5"
                  >
                    <div className="flex items-center rounded-md text-sm text-foreground transition-colors hover:bg-muted">
                      <a
                        href={entry.overviewHref}
                        onClick={closeMobile}
                        className="flex-1 rounded-l-md px-2 py-2"
                      >
                        {entry.label}
                      </a>
                      <CollapsibleTrigger
                        aria-label={messages.toggleMenu}
                        className="group flex items-center rounded-r-md px-2 py-2"
                      >
                        <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="mobile-nav-section-content">
                      <div className="mobile-nav-section-inner ml-3 flex flex-col gap-0.5 border-l border-border/60 pl-3">
                        {withGroupLabels(entry.items).map(({ item, groupLabel }) => (
                          <Fragment key={item.href}>
                            {groupLabel && (
                              <p className="px-2 pb-0.5 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/60 first:pt-0">
                                {groupLabel}
                              </p>
                            )}
                            <a
                              href={item.href}
                              onClick={closeMobile}
                              className={cn(
                                "flex flex-col rounded-md px-2 py-2 transition-colors hover:bg-muted",
                                linkActive(item.href)
                                  ? "bg-primary/5 text-primary ring-1 ring-primary/15"
                                  : "text-foreground",
                              )}
                            >
                              <span className="flex items-center gap-2 text-sm">
                                {item.label}
                                {item.flagship && (
                                  <Sparkles
                                    aria-hidden
                                    className="h-3.5 w-3.5 shrink-0 text-primary"
                                  />
                                )}
                                {item.badge && (
                                  <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-foreground/90">
                                    {item.badge}
                                  </span>
                                )}
                              </span>
                              <span
                                className={cn(
                                  "mt-0.5 text-xs",
                                  linkActive(item.href)
                                    ? "text-foreground/75"
                                    : "text-muted-foreground",
                                )}
                              >
                                {item.mobileMeta ?? item.desc}
                              </span>
                            </a>
                          </Fragment>
                        ))}
                        {entry.featured && (
                          <a
                            href={entry.featured.footerHref}
                            onClick={closeMobile}
                            className="rounded-md px-2 py-2 text-xs text-primary hover:bg-muted"
                          >
                            {entry.featured.footerLabel} →
                          </a>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ),
              )}
              <a
                href={search}
                onClick={closeMobile}
                className="rounded-md px-2 py-2 text-sm text-foreground hover:bg-muted"
              >
                {messages.navSearch}
              </a>
              <Button asChild className="mt-2 w-full">
                <a href={contactSales} onClick={closeMobile}>
                  {messages.contactSales}
                </a>
              </Button>
            </div>
          </div>
        )}
      </header>
      {open && (
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          onClick={closeMobile}
          className="mobile-nav-backdrop fixed inset-x-0 bottom-0 top-16 z-40 bg-foreground/30 animate-in fade-in-0 duration-200 lg:hidden"
        />
      )}
    </>
  );
}

/** Pairs each item with its section heading, shown once per contiguous group. */
function withGroupLabels(items: NavLeaf[]): { item: NavLeaf; groupLabel?: string }[] {
  let previous: string | undefined;
  return items.map((item) => {
    const groupLabel = item.group && item.group !== previous ? item.group : undefined;
    previous = item.group;
    return { item, groupLabel };
  });
}

/** Buckets items into contiguous runs sharing the same `group` label. */
function groupItems(items: NavLeaf[]): { label?: string; items: NavLeaf[] }[] {
  const groups: { label?: string; items: NavLeaf[] }[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.label === item.group) last.items.push(item);
    else groups.push({ label: item.group, items: [item] });
  }
  return groups;
}

function MenuPanel({
  entry,
  linkActive,
}: {
  entry: Extract<NavEntry, { kind: "menu" }>;
  linkActive: (target: string) => boolean;
}) {
  const eyebrow = (
    <p className="px-3 pb-1.5 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
      {entry.eyebrow}
    </p>
  );

  if (entry.featured) {
    const lastIndex = entry.items.length - 1;
    return (
      <div className="grid w-[34rem] grid-cols-[1.4fr_1fr]">
        <div className="p-3">
          {eyebrow}
          {entry.items.map((item, index) => (
            <Fragment key={item.href}>
              {index === lastIndex && <div className="mx-3 my-1 border-t border-border/60" />}
              <MenuRow item={item} active={linkActive(item.href)} />
            </Fragment>
          ))}
        </div>
        <FeaturedPanel featured={entry.featured} />
      </div>
    );
  }

  const groups = groupItems(entry.items);
  if (groups.some((group) => group.label)) {
    // Lay all rows on one grid (header row + one row per item slot) so the
    // items line up across columns instead of each column stacking freely.
    const rowCount = Math.max(...groups.map((group) => group.items.length));
    const cells: { key: string; col: number; label?: string; item?: NavLeaf }[] = [];
    groups.forEach((group, col) => cells.push({ key: `h${col}`, col, label: group.label }));
    for (let row = 0; row < rowCount; row++) {
      groups.forEach((group, col) => {
        const item = group.items[row];
        cells.push({ key: item ? item.href : `e${col}-${row}`, col, item });
      });
    }
    return (
      <div className="w-[46rem] p-3">
        {/* No section eyebrow here — the per-column group labels already title
            the menu (see grouped layout below). */}
        <div className="grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          {cells.map((cell) => (
            <div key={cell.key} className={cn("px-1", cell.col > 0 && "border-l border-border/60")}>
              {cell.label !== undefined ? (
                <p className="px-2 pb-1.5 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
                  {cell.label}
                </p>
              ) : cell.item ? (
                <MenuRow item={cell.item} active={linkActive(cell.item.href)} />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[36rem] p-3">
      {eyebrow}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {entry.items.map((item) => (
          <MenuRow key={item.href} item={item} active={linkActive(item.href)} />
        ))}
      </div>
    </div>
  );
}

function MenuRow({ item, active }: { item: NavLeaf; active: boolean }) {
  const Icon = item.icon;
  return (
    <NavigationMenuLink asChild>
      <a
        href={item.href}
        className="flex items-start gap-2 rounded-lg px-2 py-2.5 outline-none transition-colors hover:bg-muted/60 focus-visible:bg-muted/60"
      >
        {Icon && (
          <Icon
            className={cn(
              "mt-0.5 h-[18px] w-[18px] shrink-0 text-muted-foreground",
              active && "text-primary",
            )}
          />
        )}
        <span className="min-w-0">
          <span className="flex items-center gap-2">
            <span className={cn("text-sm font-medium text-foreground", active && "text-primary")}>
              {item.label}
            </span>
            {item.flagship && (
              <Sparkles aria-hidden className="h-3.5 w-3.5 shrink-0 text-primary" />
            )}
            {active && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />}
            {item.badge && (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-foreground/90">
                {item.badge}
              </span>
            )}
          </span>
          <span
            className={cn(
              "mt-0.5 block text-xs text-muted-foreground",
              active && "text-foreground/75",
            )}
          >
            {item.desc}
          </span>
        </span>
      </a>
    </NavigationMenuLink>
  );
}

function FeaturedPanel({ featured }: { featured: NavFeatured }) {
  return (
    <div className="flex flex-col border-l border-border/60 bg-muted/30 p-3">
      <NavigationMenuLink asChild>
        <a href={featured.href} className="group/feat flex flex-1 flex-col outline-none">
          <span className="block min-h-[8rem] w-full flex-1 overflow-hidden rounded-lg border border-border/60">
            <img
              src={featured.image}
              alt={featured.imageAlt}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover/feat:scale-105"
            />
          </span>
          <span className="mt-3 block text-sm font-medium text-foreground">{featured.title}</span>
          <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
            {featured.desc}
          </span>
          <span className="mt-2 flex items-center gap-1.5 text-xs font-medium text-primary">
            {featured.ctaLabel}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/feat:translate-x-0.5" />
          </span>
        </a>
      </NavigationMenuLink>
      <div className="mt-3 border-t border-border/60">
        <NavigationMenuLink asChild>
          <a
            href={featured.footerHref}
            className="flex items-center justify-between py-2.5 text-xs text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:text-foreground"
          >
            <span>{featured.footerLabel}</span>
            <ArrowRight className="h-3.5 w-3.5 text-primary" />
          </a>
        </NavigationMenuLink>
      </div>
    </div>
  );
}
